import { Link } from "wouter";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "./reveal";
import { SaunaSlideshow } from "./sauna-slideshow";

const base = import.meta.env.BASE_URL;

const SAUNA = [
  { href: "/sauna/advise-design", img: `${base}images/sauna-detail-3.webp`, title: "Advise & Design", desc: "Room sizing, heater output, ventilation and controls planned around your space." },
  { href: "/sauna/supply", img: `${base}images/sauna-supply-delivery.webp`, title: "Supply", desc: "Heaters, controls, ventilation components and replacement parts." },
  { href: "/sauna/install", img: `${base}images/sauna-assembly.webp`, title: "Install", desc: "Heaters, controls and ventilation fitted, commissioned and handed over." },
  { href: "/sauna/maintenance-repair", img: `${base}images/sauna-fitting.webp`, title: "Maintenance & Repair", desc: "Servicing, diagnostics and repairs for heaters, controls and ventilation." },
];

const SETTINGS = ["Traditional", "Infrared", "Commercial", "Outdoor"];

export function SaunaServices() {
  return (
    <div id="home-sauna" className="mt-20 scroll-mt-28" aria-labelledby="sauna-services-heading">
      <div className="flex items-center gap-4 mb-6">
        <h3 id="sauna-services-heading" className="font-display text-xl font-bold text-[hsl(var(--vx-ink))]">Sauna</h3>
        <span className="flex-1 h-px bg-[hsl(var(--vx-mist))]" />
      </div>

      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-[hsl(var(--vx-ink))] text-white">
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-24 w-[34rem] h-[34rem] rounded-full bg-[radial-gradient(circle,hsl(var(--vx-ember)/0.28),transparent_65%)]" />
          <div className="relative grid lg:grid-cols-[1.05fr_1fr]">
            {/* Lead photo */}
            <div className="relative min-h-[18rem] sm:min-h-[24rem] lg:min-h-full overflow-hidden">
              <SaunaSlideshow />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-transparent via-transparent to-[hsl(var(--vx-ink))]" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[hsl(var(--vx-ink)/0.85)] via-transparent to-transparent lg:from-[hsl(var(--vx-ink)/0.6)]" />
              <div className="absolute left-6 bottom-6 right-6 flex flex-wrap gap-2">
                {SETTINGS.map((s) => (
                  <span key={s} className="rounded-full bg-[hsl(var(--vx-ink)/0.55)] backdrop-blur-md px-3 py-1 text-[0.7rem] font-semibold tracking-[0.14em] uppercase text-white/90 ring-1 ring-white/15">{s}</span>
                ))}
              </div>
            </div>

            {/* Copy + services */}
            <div className="p-6 sm:p-10 lg:p-12">
              <p className="vx-eyebrow text-[hsl(var(--vx-ember))] mb-4">Custom-sized saunas · UK-wide</p>
              <h4 className="vx-display text-4xl md:text-5xl font-bold mb-5">Heat, engineered properly.</h4>
              <p className="text-white/70 leading-relaxed max-w-lg mb-8">
                Traditional, infrared, commercial and outdoor saunas, sized around your space. We use Harvia sauna heaters, with two-heater options where suitable for the design.
              </p>

              <ul className="divide-y divide-white/10 border-y border-white/10 mb-9">
                {SAUNA.map((s, i) => (
                  <li key={s.href}>
                    <Link href={s.href} data-testid={`card-sauna-${i}`}
                      className="group flex items-center gap-4 py-4 rounded-xl -mx-2 px-2 transition-colors hover:bg-white/[0.04]">
                      <span className="relative shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden ring-1 ring-white/10">
                        <img src={s.img} alt="" width={160} height={160} loading="lazy" decoding="async"
                          className="w-full h-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-110" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-baseline gap-3">
                          <span className="font-display text-xs font-semibold tracking-[0.16em] text-[hsl(var(--vx-ember))]">0{i + 1}</span>
                          <span className="font-display text-lg sm:text-xl font-bold tracking-tight">{s.title}</span>
                        </span>
                        <span className="block text-sm text-white/60 leading-relaxed mt-0.5">{s.desc}</span>
                      </span>
                      <span className="shrink-0 grid place-items-center w-10 h-10 rounded-full ring-1 ring-white/20 text-white transition-colors duration-300 group-hover:bg-[hsl(var(--vx-ember))] group-hover:ring-[hsl(var(--vx-ember))]">
                        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 motion-safe:group-hover:translate-x-0.5 motion-safe:group-hover:-translate-y-0.5" aria-hidden="true" />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col xl:flex-row gap-3">
                <Link href="/sauna/install" data-testid="button-sauna-install"
                  className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[hsl(var(--vx-ember))] text-white font-semibold transition-colors hover:bg-[hsl(18_92%_52%)]">
                  Explore sauna installation
                  <ArrowRight className="w-4 h-4 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
                </Link>
                <Link href="/quote?category=sauna" data-testid="button-sauna-quote"
                  className="inline-flex items-center justify-center h-12 px-6 rounded-full ring-1 ring-white/25 text-white font-semibold transition-colors hover:bg-white hover:text-[hsl(var(--vx-ink))]">
                  Get a sauna quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
